import { apiClient } from '../utils/api';

export type TaskType =
  | 'PIERCING_ASSIGNMENT'
  | 'DOCUMENT_REVIEW'
  | 'CONSENT_FORM'
  | 'DEPOSIT_FOLLOW_UP'
  | 'APPOINTMENT_PREPARATION'
  | 'PRACTITIONER_ONBOARDING'
  | 'CUSTOM';

export type TaskStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';

export interface TaskUser {
  id: string;
  email: string;
  userType: string;
}

export interface Task {
  id: string;
  title: string;
  taskType: TaskType;
  description: string | null;
  status: TaskStatus;
  startDate: string | null;
  endDate: string | null;
  effectiveEndDate: string | null;
  url: string | null;
  assignedToId: string;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  assignedTo: TaskUser;
  createdBy: TaskUser | null;
}

export interface CreateTaskData {
  title: string;
  taskType?: TaskType;
  description?: string | null;
  status?: TaskStatus;
  startDate?: string | null;
  endDate?: string | null;
  effectiveEndDate?: string | null;
  url?: string | null;
  assignedToId: string;
}

export interface UpdateTaskData extends Partial<CreateTaskData> {}

export interface GetTasksParams {
  assignedToId?: string;
  status?: TaskStatus;
  taskType?: TaskType;
  includeDeleted?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

export async function getTasks(params: GetTasksParams = {}): Promise<Task[]> {
  const q = new URLSearchParams();
  if (params.assignedToId) q.append('assignedToId', params.assignedToId);
  if (params.status) q.append('status', params.status);
  if (params.taskType) q.append('taskType', params.taskType);
  if (params.includeDeleted) q.append('includeDeleted', 'true');

  const response = await apiClient(`/api/tasks?${q}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).message || 'Failed to fetch tasks');
  }
  const data: ApiResponse<{ tasks: Task[] }> = await response.json();
  if (!data.success) throw new Error(data.message || 'Failed to fetch tasks');
  return data.data?.tasks || [];
}

export async function getMyTasks(params: Omit<GetTasksParams, 'assignedToId'> = {}): Promise<Task[]> {
  const q = new URLSearchParams();
  if (params.status) q.append('status', params.status);
  if (params.taskType) q.append('taskType', params.taskType);

  const response = await apiClient(`/api/tasks/my-tasks?${q}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).message || 'Failed to fetch tasks');
  }
  const data: ApiResponse<{ tasks: Task[] }> = await response.json();
  if (!data.success) throw new Error(data.message || 'Failed to fetch tasks');
  return data.data?.tasks || [];
}

export async function getTask(id: string): Promise<Task> {
  const response = await apiClient(`/api/tasks/${id}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).message || 'Failed to fetch task');
  }
  const data: ApiResponse<{ task: Task }> = await response.json();
  if (!data.success || !data.data?.task) throw new Error(data.message || 'Failed to fetch task');
  return data.data.task;
}

export async function createTask(payload: CreateTaskData): Promise<Task> {
  const response = await apiClient('/api/tasks', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).message || 'Failed to create task');
  }
  const data: ApiResponse<{ task: Task }> = await response.json();
  if (!data.success || !data.data?.task) throw new Error(data.message || 'Failed to create task');
  return data.data.task;
}

export async function updateTask(id: string, payload: UpdateTaskData): Promise<Task> {
  const response = await apiClient(`/api/tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).message || 'Failed to update task');
  }
  const data: ApiResponse<{ task: Task }> = await response.json();
  if (!data.success || !data.data?.task) throw new Error(data.message || 'Failed to update task');
  return data.data.task;
}

export async function deleteTask(id: string): Promise<void> {
  const response = await apiClient(`/api/tasks/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).message || 'Failed to delete task');
  }
  const data: ApiResponse<void> = await response.json();
  if (!data.success) throw new Error(data.message || 'Failed to delete task');
}

export async function hardDeleteTask(id: string): Promise<void> {
  const response = await apiClient(`/api/tasks/${id}?hardDelete=true`, { method: 'DELETE' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).message || 'Failed to permanently delete task');
  }
  const data: ApiResponse<void> = await response.json();
  if (!data.success) throw new Error(data.message || 'Failed to permanently delete task');
}

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  PIERCING_ASSIGNMENT: 'Attribution perceur',
  DOCUMENT_REVIEW: 'Vérification document',
  CONSENT_FORM: 'Formulaire de consentement',
  DEPOSIT_FOLLOW_UP: 'Suivi acompte',
  APPOINTMENT_PREPARATION: 'Préparation RDV',
  PRACTITIONER_ONBOARDING: 'Intégration praticien',
  CUSTOM: 'Personnalisé',
};

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  PENDING: 'En attente',
  IN_PROGRESS: 'En cours',
  DONE: 'Terminé',
  CANCELLED: 'Annulé',
};

export const TASK_STATUS_BADGE: Record<TaskStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  DONE: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
};
